// Builds the embed snippet partners copy into their site. Only emits the
// init() options that diverge from defaults so the snippet stays minimal.
//
// The widget SDK accepts `league` + `apiBase` today; `theme`, `maxWidth`,
// `oddsFormat`, and `sportsbooks` are forward-looking — the customizer
// shapes the snippet for them now and the SDK can pick them up when those
// options land. Until then, partners using the customizer fields just get
// a slightly larger snippet that the widget ignores extras on.

import { API_BASE } from './api'

export type Theme = 'default' | 'light' | 'dark'
export type Size = 'auto' | '480' | '720' | '1240'
export type OddsFormat = 'american' | 'decimal'
export type Sportsbook = 'FanDuel' | 'Bet365' | 'DraftKings' | 'Polymarket'

export const DEFAULT_BOOKS: Sportsbook[] = ['FanDuel', 'Bet365', 'DraftKings', 'Polymarket']

export interface SnippetOptions {
  theme: Theme
  size: Size
  format: OddsFormat
  books: Sportsbook[]
}

export function buildSnippet(slug: string, opts: SnippetOptions, subAffiliate?: string): string {
  const extras: string[] = []
  if (subAffiliate)
    extras.push(`    subAffiliate: '${subAffiliate}'`)
  if (opts.theme !== 'default')
    extras.push(`    theme: '${opts.theme}'`)
  if (opts.size !== 'auto')
    extras.push(`    maxWidth: ${opts.size}`)
  if (opts.format !== 'american')
    extras.push(`    oddsFormat: '${opts.format}'`)
  if (opts.books.length !== DEFAULT_BOOKS.length)
    extras.push(`    sportsbooks: [${opts.books.map(b => `'${b}'`).join(', ')}]`)

  const baseLines = [
    `<script src="${API_BASE}/widget.umd.js"></script>`,
    `<div id="odds-widget"></div>`,
    `<script>`,
    `  OddsWidget.init('#odds-widget', {`,
    `    league: '${slug}',`,
    `    apiBase: '${API_BASE}'${extras.length ? ',' : ''}`,
  ]
  if (extras.length)
    baseLines.push(extras.join(',\n'))
  baseLines.push('  })')
  baseLines.push('</script>')
  return baseLines.join('\n')
}

// Approvals embed snippet — JS-SDK pattern. Renders two semantic <ul>s
// (US states + international regions) inside Shadow DOM.
export function buildApprovalsSnippet(slug: string): string {
  return [
    `<script src="${API_BASE}/approvals-widget.umd.js"></script>`,
    `<div id="ad-approvals-${slug}"></div>`,
    `<script>`,
    `  ApprovalsWidget.init('#ad-approvals-${slug}', {`,
    `    sport: '${slug}',`,
    `    apiBase: '${API_BASE}'`,
    `  })`,
    `</script>`,
  ].join('\n')
}

// Distribution embed snippet — same JS-SDK pattern as the odds widget
// (script tag + init()), separate UMD bundle. Mounts inside Shadow DOM
// so host-page CSS can't leak in. Partners who only want carriers don't
// pay for the odds-widget bundle's Tailwind / react-query / bet slip.
// maxBooks caps the grid at the top N books (API order is priority-
// ranked); omitted from the snippet when unset so defaults stay minimal.
export function buildDistributionSnippet(slug: string, maxBooks?: number, subAffiliate?: string): string {
  const extras: string[] = []
  if (maxBooks)
    extras.push(`    maxBooks: ${maxBooks}`)
  if (subAffiliate)
    extras.push(`    subAffiliate: '${subAffiliate}'`)
  return [
    `<script src="${API_BASE}/distribution-widget.umd.js"></script>`,
    `<div id="ad-distribution-${slug}"></div>`,
    `<script>`,
    `  DistributionWidget.init('#ad-distribution-${slug}', {`,
    `    sport: '${slug}',`,
    `    apiBase: '${API_BASE}'${extras.length ? ',' : ''}`,
    ...(extras.length ? [extras.join(',\n')] : []),
    `  })`,
    `</script>`,
  ].join('\n')
}

// Approval Map embed snippet — same JS-SDK pattern. Renders the availability
// map (USA / Global / Europe / List View tabs, betting/DFS/prediction layers
// from distribution approvals) with per-place book popovers, inside Shadow DOM.
export function buildApprovalMapSnippet(slug: string, subAffiliate?: string): string {
  return [
    `<script src="${API_BASE}/approval-map-widget.umd.js"></script>`,
    `<div id="ad-approval-map-${slug}"></div>`,
    `<script>`,
    `  ApprovalMapWidget.init('#ad-approval-map-${slug}', {`,
    `    sport: '${slug}',`,
    `    apiBase: '${API_BASE}'${subAffiliate ? ',' : ''}`,
    ...(subAffiliate ? [`    subAffiliate: '${subAffiliate}'`] : []),
    `  })`,
    `</script>`,
  ].join('\n')
}

// ── Sportsbook-keyed twins ───────────────────────────────────────────
// Same bundles, `sportsbook:` instead of `sport:` (see each SDK's
// config). The DOM ids carry a `book-` prefix so a page embedding both a
// sport's and a book's widget never collides.

// CARRIED SPORTS — the sports a book carries as plain sport-logo tiles (no
// links, no brand backgrounds — Cole 2026-10-07). maxBooks caps the count.
export function buildSportsbookDistributionSnippet(slug: string, maxBooks?: number): string {
  const extras: string[] = []
  if (maxBooks)
    extras.push(`    maxBooks: ${maxBooks}`)
  return [
    `<script src="${API_BASE}/distribution-widget.umd.js"></script>`,
    `<div id="ad-distribution-book-${slug}"></div>`,
    `<script>`,
    `  DistributionWidget.init('#ad-distribution-book-${slug}', {`,
    `    sportsbook: '${slug}',`,
    `    apiBase: '${API_BASE}'${extras.length ? ',' : ''}`,
    ...(extras.length ? [extras.join(',\n')] : []),
    `  })`,
    `</script>`,
  ].join('\n')
}

// AVAILABILITY — where the book is live: US states, international
// allow-list, and (deny-list operators) the regions it is not available in.
export function buildSportsbookApprovalsSnippet(slug: string): string {
  return [
    `<script src="${API_BASE}/approvals-widget.umd.js"></script>`,
    `<div id="ad-approvals-book-${slug}"></div>`,
    `<script>`,
    `  ApprovalsWidget.init('#ad-approvals-book-${slug}', {`,
    `    sportsbook: '${slug}',`,
    `    apiBase: '${API_BASE}'`,
    `  })`,
    `</script>`,
  ].join('\n')
}

// AVAILABILITY MAP — the book's reach painted in its brand colour; each
// place's popover lists the sports it carries that are available there
// (plain logos, not links — so no sub-affiliate flag here).
export function buildSportsbookMapSnippet(slug: string): string {
  return [
    `<script src="${API_BASE}/approval-map-widget.umd.js"></script>`,
    `<div id="ad-approval-map-book-${slug}"></div>`,
    `<script>`,
    `  ApprovalMapWidget.init('#ad-approval-map-book-${slug}', {`,
    `    sportsbook: '${slug}',`,
    `    apiBase: '${API_BASE}'`,
    `  })`,
    `</script>`,
  ].join('\n')
}

// Iframe body is transparent so the portal's iframe element bg (driven by
// var(--paper)) shows through. When the portal is in dark mode the mount
// div carries data-asd-theme="dark" — the same detection contract NXTbets
// pages use — so the widget renders its dark theme. The SDK resolves the
// theme once at mount, which is why the iframe doc (and therefore the
// widget) reloads on theme toggle rather than restyling in place.
export function buildPreviewDoc(slug: string, dark = false, subAffiliate?: string): string {
  const aff = subAffiliate ? `,subAffiliate:'${subAffiliate}'` : ''
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>html,body{margin:0;padding:12px;font-family:Poppins,sans-serif;background:transparent}</style>
</head>
<body>
  <div id="odds-widget"${dark ? ' data-asd-theme="dark"' : ''}></div>
  <script src="${API_BASE}/widget.umd.js" onload="OddsWidget.init('#odds-widget',{league:'${slug}',apiBase:'${API_BASE}'${aff}})"></script>
</body>
</html>`
}

// (The overview's odds dashboard mounts the SDK directly on the portal
// page — see App.tsx ensureOddsWidgetScript/injectNativeSkin — so there is
// deliberately no dashboard snippet builder here.)
